import { useRef } from 'react';
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll';
import AnimatedBackground from './AnimatedBackground';

export default function HeroSection() {
  const contentRef = useRef<HTMLDivElement>(null);
  useRevealOnScroll(contentRef);

  return (
    <section className="relative w-full min-h-screen md:min-h-[90vh] flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#271403' }}>
      {/* Interactive particle canvas */}
      <AnimatedBackground mode="orbs" density={60} />

      {/* Ambient glowing orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-accent-500/18 blur-[150px] animate-pulse-glow"></div>
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] rounded-full bg-accent-500/14 blur-[120px] animate-pulse-glow-strong"></div>
        <div className="absolute top-2/3 left-1/3 w-[300px] h-[300px] rounded-full bg-accent-500/10 blur-[100px] animate-pulse-glow"></div>
      </div>

      {/* Scan-line texture overlay */}
      <div className="absolute inset-0 pointer-events-none scan-overlay opacity-20"></div>

      {/* Floating ring elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[15%] right-[10%] w-64 h-64 animate-breathe" style={{ animationDuration: '10s' }}>
          <div className="w-full h-full rounded-full border border-accent-500/12 animate-rotate-slow opacity-40"></div>
        </div>
        <div className="absolute bottom-[20%] left-[8%] w-48 h-48 animate-breathe" style={{ animationDuration: '8s', animationDelay: '2.5s' }}>
          <div className="w-full h-full rounded-full border border-accent-500/8 animate-rotate-slow opacity-30" style={{ animationDirection: 'reverse', animationDuration: '40s' }}></div>
        </div>
        <div className="absolute top-[40%] right-[25%] w-32 h-32 animate-breathe" style={{ animationDuration: '12s', animationDelay: '5s' }}>
          <div className="w-full h-full rounded-full border border-accent-500/6 animate-rotate-slow opacity-25" style={{ animationDuration: '20s' }}></div>
        </div>
      </div>

      {/* Floating dots */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[20%] left-[15%] w-1.5 h-1.5 rounded-full bg-accent-500/35 animate-float"></div>
        <div className="absolute top-[35%] right-[20%] w-1 h-1 rounded-full bg-accent-500/25 animate-float" style={{ animationDelay: '1.5s', animationDuration: '10s' }}></div>
        <div className="absolute bottom-[30%] left-[25%] w-1.5 h-1.5 rounded-full bg-accent-500/30 animate-float" style={{ animationDelay: '3s', animationDuration: '9s' }}></div>
        <div className="absolute top-[55%] right-[12%] w-1 h-1 rounded-full bg-accent-500/20 animate-float-slow"></div>
        <div className="absolute bottom-[15%] right-[30%] w-1.5 h-1.5 rounded-full bg-accent-500/30 animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      <div ref={contentRef} className="relative z-10 w-full text-center px-6 md:px-12 pt-28 pb-20">
        <div className="max-w-4xl mx-auto">
          <h1 data-reveal className="text-center">
            <span data-reveal data-reveal-delay="0.05" className="block text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tighter">
              <span className="text-metallic">Technology that generates</span>
            </span>
            <span data-reveal data-reveal-delay="0.2" className="block text-3xl md:text-4xl lg:text-5xl font-light tracking-wide text-foreground-200 mt-1 md:mt-2">
              instead of configures
            </span>
          </h1>

          <p data-reveal data-reveal-delay="0.4" className="mt-5 md:mt-6 text-base md:text-lg text-foreground-300 max-w-2xl mx-auto leading-relaxed">
            Off-the-shelf software makes you bend your business to fit it.
            <br className="hidden md:block" />
            We do the opposite — we generate software tailored to you, built around how you already work.
          </p>

          <div data-reveal data-reveal-delay="0.5" className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#build"
              className="px-8 py-3.5 rounded-full bg-accent-500 text-background-950 font-semibold text-sm md:text-base hover:bg-accent-400 hover:scale-105 hover:ring-4 hover:ring-accent-400/30 transition-all duration-300 whitespace-nowrap cursor-pointer glow-ring animate-breathe-cta"
            >
              Start Building
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}